import { useRef, useState } from 'react'
import { ImagePlus, Link2, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import {
  useAuthenticatedAssetUrl,
  useLocalFilePreview,
} from '@/components/Gestores/gestorFeedbackEvidenceUtils'
import { cn } from '@/utils/cn'

type Props = {
  pjId: string
  evidenciaUrl: string | null
  evidenciaFileName: string | null
  evidenciaLink: string
  onUpload: (file: File) => Promise<void>
  onLinkChange: (value: string) => void
  onClear: () => void
  disabled?: boolean
}

function readPastedImageFile(event: React.ClipboardEvent): File | null {
  for (const item of Array.from(event.clipboardData.items)) {
    if (item.type.startsWith('image/')) {
      const file = item.getAsFile()
      if (file) return file
    }
  }
  const fromFiles = event.clipboardData.files[0]
  if (fromFiles?.type.startsWith('image/')) return fromFiles
  return null
}

export function GestorFeedbackEvidenceField({
  evidenciaUrl,
  evidenciaFileName,
  evidenciaLink,
  onUpload,
  onLinkChange,
  onClear,
  disabled = false,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const pasteZoneRef = useRef<HTMLDivElement>(null)
  const [uploading, setUploading] = useState(false)
  const { previewUrl: localPreview, setFilePreview, clearPreview } = useLocalFilePreview()
  const serverPreview = useAuthenticatedAssetUrl(evidenciaUrl)
  const previewUrl = localPreview ?? serverPreview

  const handleFile = async (file: File | null | undefined) => {
    if (!file || disabled) return
    setFilePreview(file)
    setUploading(true)
    try {
      await onUpload(file)
    } catch {
      clearPreview()
      toast.error('Não foi possível enviar a evidência.')
    } finally {
      setUploading(false)
    }
  }

  const handlePaste = (event: React.ClipboardEvent) => {
    if (disabled) return
    const file = readPastedImageFile(event)
    if (!file) return
    event.preventDefault()
    void handleFile(file)
  }

  const handleClear = () => {
    clearPreview()
    onClear()
  }

  return (
    <div className='space-y-3'>
      <div className='flex flex-wrap gap-2'>
        <input
          ref={inputRef}
          type='file'
          accept='image/png,image/jpeg,image/webp,image/gif'
          className='hidden'
          onChange={(event) => void handleFile(event.target.files?.[0])}
        />
        <Button
          type='button'
          size='sm'
          variant='secondary'
          icon={<ImagePlus size={14} />}
          loading={uploading}
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          Enviar print
        </Button>
        {(evidenciaUrl || evidenciaLink || localPreview) && (
          <Button type='button' size='sm' variant='destructive' disabled={disabled} onClick={handleClear}>
            <Trash2 size={14} />
            Remover evidência
          </Button>
        )}
      </div>

      <Input
        label='Link da evidência (opcional)'
        value={evidenciaLink}
        onChange={(event) => onLinkChange(event.target.value)}
        placeholder='https://…'
        icon={<Link2 size={14} />}
        disabled={disabled}
      />

      <div
        ref={pasteZoneRef}
        tabIndex={disabled ? -1 : 0}
        role='button'
        aria-label='Área para colar print da evidência'
        onPaste={handlePaste}
        onClick={() => pasteZoneRef.current?.focus()}
        className={cn(
          'rounded-lg border border-dashed border-border bg-bg-main/40 outline-none transition-colors',
          'focus:border-accent focus:ring-1 focus:ring-accent/30',
          previewUrl ? 'p-2' : 'px-3 py-4',
        )}
      >
        {previewUrl ? (
          <div className='overflow-hidden rounded-md border border-border bg-bg-main'>
            <img
              src={previewUrl}
              alt={evidenciaFileName ?? 'Evidência colada'}
              className='max-h-64 w-full object-contain'
            />
            {(evidenciaFileName || uploading) && (
              <p className='border-t border-border px-3 py-2 text-xs text-text-muted'>
                {uploading ? 'Enviando evidência…' : evidenciaFileName}
              </p>
            )}
          </div>
        ) : (
          <p className='text-center text-xs text-text-muted'>
            Clique aqui e cole o print com <strong>Ctrl+V</strong>, ou use &quot;Enviar print&quot; (PNG, JPG, WEBP ou
            GIF — até 5 MB).
          </p>
        )}
      </div>
    </div>
  )
}
